import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { Metamodel } from 'app/model/base/model';
import { SalesforceMarketingCloudCopySource } from 'app/model/pipeline/copysource/salesforce-marketing-cloud.copysource';
import { SalesforceMarketingCloudLinkedService, SalesforceMarketingCloudAuthType } from 'app/model/linkedservice/salesforce-marketing-cloud.linkedservice';
import { useEncryptedEndpointsRadioSwitch } from './linked-service-shared-fields';
import { format, stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { salesforceMarketingCloudReservedKeys } from 'app/shared/utils/connector-metadata-static-consts';
import { deprecatedConnectorDocLink } from 'app/shared/constants/links.const';

export const salesforceMarketingCloudMetadata: ILinkedServiceTypeMetadata = {
    type: Metamodel.modelFor(SalesforceMarketingCloudLinkedService).name,
    displayName: format(stringResources.ModelName_DisplayTextLegacyTemplate, stringResources.SalesforceMarketingCloud_DisplayText),
    linkedServiceKind: 'table',
    icon: {
        className: 'svg-i-100x100-Salesforce'
    },
    warningMessage: stringResources.Connector_Legacy_IsDeprecatedWarning,
    warningMessageHyperlink: deprecatedConnectorDocLink,
    warningTooltip: stringResources.Connector_Legacy_IsDeprecatedHint,
    notShowInTypeSelection: true,
    isFirstClass: true,
    supportDatasetV2: true,
    category: ['ServicesApps'],
    copySinkTypes: [],
    copySourceTypes: [Metamodel.modelFor(SalesforceMarketingCloudCopySource).name],
    controls: [
        {
            type: 'select-switch',
            bind: 'authType',
            hide: '{ isOldResource }',
            label: stringResources.ModelName_AuthenticationType,
            description: stringResources.SalesforceMarketingCloud_Auth_Method_Description,
            options: [
                {
                    label: 'OAuth 2.0',
                    value: SalesforceMarketingCloudAuthType.OAuth_2_0
                },
                {
                    label: 'Enhanced sts OAuth 2.0',
                    value: SalesforceMarketingCloudAuthType.Enhanced_sts_OAuth_2_0
                }
            ],
            isLabelVertical: true,
            defaultValue: SalesforceMarketingCloudAuthType.Enhanced_sts_OAuth_2_0,
            cases: [
                {
                    caseValue: SalesforceMarketingCloudAuthType.OAuth_2_0,
                    children: [
                        {
                            type: 'textbox',
                            bind: '', // this textbox is just used to display a fixed string. Once OAuth_2.0 is choosed, 'host' must be 'www.exacttargetapis.com'
                            required: true,
                            readonly: true,
                            defaultValue: 'www.exacttargetapis.com',
                            label: stringResources.ModelName_Host,
                            isLabelVertical: true,
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ]
                        }
                    ]
                },
                {
                    caseValue: SalesforceMarketingCloudAuthType.Enhanced_sts_OAuth_2_0,
                    children: [
                        {
                            type: 'textbox',
                            bind: 'subdomain',
                            required: true,
                            label: stringResources.ModelName_Subdomain,
                            description: stringResources.SalesforceMarketingCloud_Subdomain_Description,
                            isLabelVertical: true,
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ],
                            warningMessageCalculator: (value: string) =>
                                stringIsNullOrEmpty(value) ? '' : stringResources.infoSentViaUrlWarning
                        }
                    ]
                }
            ]
        },
        {
            type: 'textbox',
            bind: 'clientId',
            required: true,
            label: stringResources.ModelName_ClientId,
            isLabelVertical: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ]
        },
        {
            type: 'dynamic-akv-switcher',
            bind: 'clientSecret',
            label: stringResources.ModelName_ClientSecret,
            child: {
                type: 'password',
                required: true,
                label: stringResources.ModelName_ClientSecret,
                isLabelVertical: true
            },
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                stringResources.SalesforceMarketingCloud_DisplayText,
                stringResources.ModelName_ClientSecret)
        },
        useEncryptedEndpointsRadioSwitch,
        {
            type: 'key-val-list',
            bind: 'additionalFields',
            label: stringResources.AdditionalConnectionStringFields,
            hide: '{ isOldResource }',
            reservedKeys: salesforceMarketingCloudReservedKeys
        }
    ]
};
